import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Send, Users, Mail, Loader2, Clock, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterComposer = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    html_content: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/newsletter/subscribers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/newsletter/history`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSubscribers(subsRes.data);
      setHistory(histRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendNewsletter = async () => {
    if (!formData.subject || !formData.html_content) {
      toast.error('Please fill in subject and content');
      return;
    }

    if (subscribers.length === 0) {
      toast.error('No subscribers to send to');
      return;
    }

    if (!window.confirm(`Send newsletter to ${subscribers.length} subscribers?`)) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/newsletter/send`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 10000 });
      }
      
      if (response.data.sent > 0) {
        toast.success(`Newsletter sent to ${response.data.sent} subscribers!`);
      } else if (response.data.failed > 0) {
        toast.error(`Failed to send to ${response.data.failed} subscribers. Check if Resend domain is verified.`);
      }
      
      setFormData({ subject: '', html_content: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };

  const insertTemplate = (template) => {
    const templates = {
      welcome: `<h1 style="color: #4f46e5; margin-bottom: 16px;">Welcome to AISkillFlow!</h1>
<p style="color: #374151; font-size: 16px; line-height: 1.6;">
  Thank you for joining our community of learners. We're excited to have you!
</p>
<p style="color: #374151; font-size: 16px; line-height: 1.6;">
  Browse our latest courses and start your learning journey today.
</p>
<a href="{{site_url}}/courses" style="display: inline-block; background: #4f46e5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin-top: 16px;">
  Browse Courses
</a>`,
      newCourse: `<h1 style="color: #4f46e5; margin-bottom: 16px;">New Course Alert! 🎉</h1>
<p style="color: #374151; font-size: 16px; line-height: 1.6;">
  We just launched a new course that you might love:
</p>
<h2 style="color: #1f2937; margin: 24px 0 8px;">[Course Title]</h2>
<p style="color: #6b7280; font-size: 14px;">[Course Description]</p>
<a href="{{site_url}}/courses/[course-id]" style="display: inline-block; background: #4f46e5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin-top: 16px;">
  View Course
</a>`,
      tips: `<h1 style="color: #4f46e5; margin-bottom: 16px;">Weekly Learning Tips 📚</h1>
<p style="color: #374151; font-size: 16px; line-height: 1.6;">
  Here are this week's top tips to maximize your learning:
</p>
<ul style="color: #374151; font-size: 16px; line-height: 1.8; padding-left: 20px;">
  <li>Set aside dedicated learning time each day</li>
  <li>Take notes while watching video lessons</li>
  <li>Practice what you learn with real projects</li>
  <li>Join our community discussions</li>
</ul>
<p style="color: #374151; font-size: 16px; line-height: 1.6; margin-top: 16px;">
  Happy learning!
</p>`
    };
    setFormData({ ...formData, html_content: templates[template] || '' });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="newsletter-composer">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate('/admin/messages')} className="-ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Newsletter Composer</h1>
              <p className="text-slate-600">Send emails to your subscribers</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Composer */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Compose Newsletter
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject Line</Label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Your newsletter subject"
                      data-testid="newsletter-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Email Content (HTML)</Label>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => insertTemplate('welcome')}>
                          Welcome
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => insertTemplate('newCourse')}>
                          New Course
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => insertTemplate('tips')}>
                          Tips
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      value={formData.html_content}
                      onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                      placeholder="<h1>Your newsletter content...</h1>"
                      rows={15}
                      className="font-mono text-sm"
                      data-testid="newsletter-content"
                    />
                  </div>

                  <Button 
                    onClick={sendNewsletter} 
                    disabled={sending || subscribers.length === 0}
                    className="w-full"
                    data-testid="send-newsletter-btn"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send to {subscribers.length} Subscribers
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Preview */}
              {formData.html_content && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="prose prose-sm max-w-none p-4 bg-white border border-slate-200 rounded-lg"
                      dangerouslySetInnerHTML={{ __html: formData.html_content }}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Stats */}
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{subscribers.length}</p>
                      <p className="text-sm text-slate-600">Active Subscribers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* History */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Recent Newsletters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length > 0 ? (
                    <div className="space-y-3">
                      {history.slice(0, 5).map((item) => (
                        <div key={item.id} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium text-slate-900 text-sm truncate">{item.subject}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              {item.sent_count} sent
                            </span>
                            <span>{new Date(item.sent_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No newsletters sent yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterComposer;
